import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin, MenuTextMatch } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { TextNode } from 'lexical';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import { useFloating, FloatingPortal, offset, flip, shift, autoUpdate } from '@floating-ui/react';

import { $createMentionNode, MentionNode } from './MentionNode';
import {
  MentionsMenu,
  MentionsMenuItem,
  MentionsMenuGroupHeader,
} from '@mutiny-pkg/bin-ui/src/richtext/MentionsPlugin/MentionsMenu';
import { MentionTypeaheadOption } from '@mutiny-pkg/bin-ui/src/richtext/MentionsPlugin/MentionTypeaheadOption';
import {
  $createExtendedTextNode,
  ExtendedTextNode,
} from '@mutiny-pkg/bin-ui/src/richtext/ExtendedTextPlugin/ExtendedTextNode';
import { IconType } from '@mutiny-pkg/dumpster-ui/icons';

export type MentionItem = {
  label: string;
  value: string;
  groupLabel?: string;
  completeness?: number;
  IconComponent?: IconType;
};

const PUNCTUATION = '\\.,\\+\\*\\?\\$\\@\\|#{}\\(\\)\\^\\-\\[\\]\\\\/!%\'"~=<>_:;';
const NAME = `\\b[A-Z][^\\s${PUNCTUATION}]`;

const DocumentMentionsRegex = {
  NAME,
  PUNCTUATION,
};

const PUNC = DocumentMentionsRegex.PUNCTUATION;

const TRIGGERS = ['@'].join('');

// Chars we expect to see in a mention (non-space, non-punctuation).
const VALID_CHARS = `[^${TRIGGERS}${PUNC}\\s]`;

// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
const VALID_JOINS =
  '(?:' +
  '\\.[ |$]|' + // E.g. "r. " in "Mr. Smith"
  ' |' + // E.g. " " in "Josh Duck"
  `[${PUNC}]|` + // E.g. "-' in "Salier-Hellendag"
  ')';

const LENGTH_LIMIT = 75;

const AtSignMentionsRegex = new RegExp(`(.?)([${TRIGGERS}]((?:${VALID_CHARS}${VALID_JOINS}){0,${LENGTH_LIMIT}})?)$`);

// 50 is the longest alias length limit.
const ALIAS_LENGTH_LIMIT = 50;

// Regex used to match alias.
const AtSignMentionsRegexAliasRegex = new RegExp(`(.?)([${TRIGGERS}]((?:${VALID_CHARS}){0,${ALIAS_LENGTH_LIMIT}})?)$`);

function checkForAtSignMentions(text: string, minMatchLength: number): MenuTextMatch | null {
  let match = AtSignMentionsRegex.exec(text);

  if (match === null) {
    match = AtSignMentionsRegexAliasRegex.exec(text);
  }
  if (match !== null) {
    // The strategy ignores leading whitespace but we need to know it's
    // length to add it to the leadOffset
    const maybeLeadingWhitespace = match[1];

    const matchingString = match[3];
    const matchingStringWithTrigger = match[2];
    if ((matchingString && matchingString.length >= minMatchLength) || matchingStringWithTrigger) {
      return {
        leadOffset: match.index + (maybeLeadingWhitespace?.length ?? 0),
        matchingString: match[3] ?? '',
        replaceableString: match[2] ?? '',
      };
    }
  }
  return null;
}

function getPossibleQueryMatch(text: string): MenuTextMatch | null {
  return checkForAtSignMentions(text, 1);
}

export function MentionsPlugin({
  items,
  onMenuOpenChange,
  emptyStateMessage,
}: {
  items: MentionItem[];
  onMenuOpenChange?: (open: boolean) => void;
  emptyStateMessage?: string;
}) {
  const [editor] = useLexicalComposerContext();

  const [queryString, setQueryString] = useState<string | null>(null);

  // The value of a mention node can be a foreign key/pointer to a external data entity. For that reason, we only store
  // normalized version of the node (just the value). We need to rehydrate the label when the node is created in the editor.
  //
  // An example is Slacks' mention. In a text "Hello @darren," the mention would point to a user object.
  // If later the user decided to change their nickname to @daz, the mention node would still be valid and render
  // "Hello @daz," instead.
  useEffect(() => {
    const itemByValue = Object.fromEntries(items.map((item) => [item.value, item]));
    return editor.registerNodeTransform(MentionNode, (node) => {
      // If the node becomes inconsistent, replace it with a normal text node instead.
      if (!node.isConsistent()) {
        node.replace($createExtendedTextNode(node.getTextContent()));
        return;
      }

      // Hydrate the label and icon
      const item = itemByValue[node.getValue()];
      if (item) {
        node.setLabel(item.label);
        if (item.IconComponent) {
          node.setIconComponent(item.IconComponent);
        }
      }
    });
  }, [editor, items]);

  // Menu options
  const options = useMemo(
    () =>
      items
        .map(
          (item) =>
            new MentionTypeaheadOption(
              item.label ?? item.value,
              item.value,
              item.IconComponent,
              item.completeness,
              item.groupLabel,
            ),
        )
        .filter((item) => (queryString ? item.label.toLowerCase().includes(queryString?.toLowerCase() ?? '') : true))
        .sort((a, b) => {
          // First sort by group (alphabetically)
          const aGroup = a.groupLabel || 'Other';
          const bGroup = b.groupLabel || 'Other';
          if (aGroup !== bGroup) {
            return aGroup.localeCompare(bGroup);
          }

          // Within the same group, return prefix matches first
          // Tiebreak by increasing length
          const aStartsWithQuery = a.label.toLowerCase().startsWith(queryString?.toLowerCase() ?? '');
          const bStartsWithQuery = b.label.toLowerCase().startsWith(queryString?.toLowerCase() ?? '');
          if (aStartsWithQuery && !bStartsWithQuery) {
            return -1;
          }
          if (!aStartsWithQuery && bStartsWithQuery) {
            return 1;
          }
          const lengthDifference = a.label.length - b.label.length;
          if (lengthDifference !== 0) {
            return lengthDifference;
          }
          return a.label > b.label ? 1 : -1;
        }),
    [items, queryString],
  );

  const onSelectOption = useCallback(
    (selectedOption: MentionTypeaheadOption, nodeToReplace: TextNode | null, closeMenu: () => void) => {
      editor.update(() => {
        const mentionNode = $createMentionNode(
          selectedOption.value,
          selectedOption.label,
          selectedOption.iconComponent,
        );
        mentionNode.setFormat(nodeToReplace?.getFormat() ?? 0);
        if (nodeToReplace) {
          nodeToReplace.replace(mentionNode);
        }
        const textNode = new ExtendedTextNode(' ');
        mentionNode.insertAfter(textNode);

        textNode.select();
        closeMenu();
      });
    },
    [editor],
  );

  const checkForMentionMatch = useCallback((text: string) => {
    return getPossibleQueryMatch(text);
  }, []);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const { x, y, refs, strategy, update } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  return (
    <LexicalTypeaheadMenuPlugin<MentionTypeaheadOption>
      onQueryChange={setQueryString}
      onOpen={(resolution) => {
        setIsMenuOpen(true);
        onMenuOpenChange?.(true);
        // Update floating position based on Lexical's resolution
        refs.setPositionReference({
          getBoundingClientRect: resolution.getRect,
        });
        update();
      }}
      onClose={() => {
        setIsMenuOpen(false);
        onMenuOpenChange?.(false);
      }}
      onSelectOption={onSelectOption}
      triggerFn={checkForMentionMatch}
      options={options}
      anchorClassName="mentions-reset-anchor"
      menuRenderFn={(anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
        if (!anchorElementRef.current || !isMenuOpen) return null;
        if (!items.length && !emptyStateMessage) return null;

        // Compute group labels
        const groupLabels = Array.from(new Set(options.map((option) => option.groupLabel ?? '')));
        const onlyOneGroup = groupLabels.length <= 1;

        return (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                width: 'max-content',
                zIndex: 10001,
              }}
            >
              <MentionsMenu>
                {options.length === 0 ? (
                  <li style={{ padding: '8px', color: '#888' }}>{emptyStateMessage || 'No results found'}</li>
                ) : (
                  options.map((option, index) => {
                    const isFirstInGroup = index === 0 || options[index - 1]?.groupLabel !== option.groupLabel;
                    const currentGroup = option.groupLabel;

                    return (
                      <React.Fragment key={option.key}>
                        {!onlyOneGroup && isFirstInGroup && <MentionsMenuGroupHeader groupLabel={currentGroup} />}
                        <MentionsMenuItem
                          index={index}
                          isSelected={selectedIndex === index}
                          onClick={() => {
                            setHighlightedIndex(index);
                            selectOptionAndCleanUp(option);
                          }}
                          onMouseEnter={() => {
                            setHighlightedIndex(index);
                          }}
                          onMouseDown={(event) => {
                            // Clicking on a mention should not cause the editor to lose focus because we are
                            // still in the interacting and editing. Stealing focus from the editor, even temporarily,
                            // can cause both visual issues, e.g. focus state flashes, or logical errors, e.g. when the
                            // caller relies on the blur event to auto save changes.
                            //
                            // This prevents the mouse down event from stealing the input.
                            event.preventDefault();
                          }}
                          key={option.key}
                          option={option}
                        />
                      </React.Fragment>
                    );
                  })
                )}
              </MentionsMenu>
            </div>
          </FloatingPortal>
        );
      }}
    />
  );
}
